/**
 * Pure helpers for the simple Content Studio (claude_worker and hybrid workspaces).
 *
 * Everything here is deliberately defensive: the list endpoint and the detail endpoint do not
 * return the same columns, and worker packages come in two shapes (caption = whole markdown, or
 * caption = title + documentContent = body). Nothing in this file touches the network.
 */
import type { ContentItem } from "@shared/schema";

/** A content row as the studio sees it. `title` / `wordCount` are optional extras the server may add. */
export type StudioItem = ContentItem & { title?: string | null; wordCount?: number | null };

// ── Classification ──────────────────────────────────────────────────────────

export type ItemKind = "article" | "social" | "email" | "ad" | "plan" | "brief" | "other";

const SOCIAL_PLATFORMS = ["linkedin", "x", "twitter", "facebook", "instagram", "tiktok", "threads", "youtube"];

const PLATFORM_LABEL: Record<string, string> = {
  linkedin: "LinkedIn",
  x: "X",
  twitter: "X",
  facebook: "Facebook",
  instagram: "Instagram",
  tiktok: "TikTok",
  threads: "Threads",
  youtube: "YouTube",
};

export function platformKey(item: Pick<StudioItem, "platform">): string {
  return (item.platform || "").trim().toLowerCase();
}

export function kindOf(item: StudioItem): ItemKind {
  const p = platformKey(item);
  const ct = (item.contentType || "").trim().toLowerCase();
  const t = (item.type || "").trim().toLowerCase();
  if (ct === "blog" || p === "blog" || p === "wordpress" || t === "article" || t === "blog") return "article";
  if (p === "email" || ct === "email") return "email";
  if (p === "ad" || p === "meta" || p === "google" || ct === "ad") return "ad";
  if (p === "plan" || ct === "plan") return "plan";
  if (p === "brief" || ct === "brief") return "brief";
  if (SOCIAL_PLATFORMS.includes(p)) return "social";
  return "other";
}

/** Can this kind be published or scheduled from the studio? Everything else is a working document. */
export function isPublishable(kind: ItemKind): boolean {
  return kind === "article" || kind === "social";
}

export function typeLabel(item: StudioItem): string {
  const kind = kindOf(item);
  switch (kind) {
    case "article": return "Article";
    case "email": return "Email campaign";
    case "ad": return "Ad";
    case "plan": return "Plan";
    case "brief": return "Brief";
    case "social": return `${PLATFORM_LABEL[platformKey(item)] ?? "Social"} post`;
    default: return "Other";
  }
}

export function platformLabel(item: StudioItem): string {
  const p = platformKey(item);
  return PLATFORM_LABEL[p] ?? typeLabel(item);
}

/** Order + labels used by the inbox grouping. */
export const KIND_GROUPS: { id: string; label: string; kinds: ItemKind[] }[] = [
  { id: "article", label: "Articles", kinds: ["article"] },
  { id: "social", label: "Social posts", kinds: ["social"] },
  { id: "email", label: "Email campaigns", kinds: ["email"] },
  { id: "ad", label: "Ads", kinds: ["ad"] },
  { id: "doc", label: "Plans & briefs", kinds: ["plan", "brief"] },
  { id: "other", label: "Other", kinds: ["other"] },
];

// ── Agent ───────────────────────────────────────────────────────────────────

export interface AgentInfo {
  name: string;
  /** True when the row was written by the external Claude worker (sourceAgent = external_api). */
  viaWorker: boolean;
}

const KNOWN_AGENTS = ["peggy", "pete", "harry", "rogers", "draper"];

export function agentFor(item: StudioItem): AgentInfo {
  const sa = String(item.sourceAgent || "").trim().toLowerCase();
  const viaWorker = sa === "external_api";
  const known = KNOWN_AGENTS.find((a) => sa.includes(a));
  if (known) return { name: known[0].toUpperCase() + known.slice(1), viaWorker };
  switch (kindOf(item)) {
    case "ad": return { name: "Pete", viaWorker };
    case "brief": return { name: "Harry", viaWorker };
    case "email": return { name: "Rogers", viaWorker };
    case "plan":
    case "article":
    case "social": return { name: "Peggy", viaWorker };
    default: return { name: "Draper", viaWorker };
  }
}

export function agentLabel(item: StudioItem): string {
  const a = agentFor(item);
  return a.viaWorker ? `${a.name} (Claude team)` : a.name;
}

// ── Origin (who produced it) ────────────────────────────────────────────────

export type OriginId = "claude" | "gemini" | "you";

export interface Origin {
  id: OriginId;
  label: string;
  /** Tooltip text explaining the label. */
  title: string;
}

const ORIGINS: Record<OriginId, Origin> = {
  claude: { id: "claude", label: "Claude team", title: "Written by the Claude team (an external worker)" },
  gemini: { id: "gemini", label: "Gemini", title: "Written by Draper's built-in Gemini agents" },
  you: { id: "you", label: "You", title: "Created by hand in Content Studio" },
};

/**
 * Who produced an item.
 *  - sourceAgent "external_api" (the v1 worker API) or an MCP/Claude client: Claude team.
 *  - any other named source agent: Gemini (the in-process agents).
 *  - no sourceAgent: scheduled/automatic generators leave a category, quality score or plan id behind (Gemini);
 *    a row with none of those was typed or saved by hand (You). This last rule is a heuristic, not a stored fact.
 */
export function originOf(item: StudioItem): Origin {
  const sa = String(item.sourceAgent || "").trim().toLowerCase();
  if (sa) {
    if (sa === "external_api" || sa === "mcp_agent" || sa.includes("claude") || sa.includes("worker")) return ORIGINS.claude;
    return ORIGINS.gemini;
  }
  const generated = item.qualityScore != null || !!item.contentCategory || item.planId != null;
  return generated ? ORIGINS.gemini : ORIGINS.you;
}

export function originClasses(id: OriginId): string {
  switch (id) {
    case "claude": return "bg-violet-500/15 text-violet-300 border-violet-500/30";
    case "gemini": return "bg-sky-500/15 text-sky-300 border-sky-500/30";
    case "you": return "bg-zinc-500/15 text-zinc-300 border-zinc-500/30";
  }
}

export const AGENT_FILTERS = ["Peggy", "Pete", "Harry", "Rogers", "Draper"] as const;

// ── Dates ───────────────────────────────────────────────────────────────────

export function toDate(v: unknown): Date | null {
  if (!v) return null;
  const d = v instanceof Date ? v : new Date(v as string | number);
  return Number.isNaN(d.getTime()) ? null : d;
}

export function fmtDate(v: unknown): string {
  const d = toDate(v);
  return d ? d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }) : "";
}

export function fmtDateTime(v: unknown): string {
  const d = toDate(v);
  return d
    ? d.toLocaleString(undefined, { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" })
    : "";
}

export function fmtDayHeading(d: Date): string {
  return d.toLocaleDateString(undefined, { weekday: "long", month: "short", day: "numeric" });
}

export function fmtTime(v: unknown): string {
  const d = toDate(v);
  return d ? d.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" }) : "";
}

export function startOfDay(d: Date): Date {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
}

export function dayKey(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

/** Value for <input type="datetime-local"> in the viewer's local time. */
export function toLocalInputValue(d: Date): string {
  return `${dayKey(d)}T${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
}

export function tomorrowAtNine(): string {
  const d = new Date();
  d.setDate(d.getDate() + 1);
  d.setHours(9, 0, 0, 0);
  return toLocalInputValue(d);
}

// ── Stage (plain-language state) ────────────────────────────────────────────

export type Stage = "needs_review" | "approved" | "scheduled" | "overdue" | "published" | "failed" | "rejected";

export function isPending(item: StudioItem): boolean {
  const a = (item.approvalStatus || "").toLowerCase();
  return a === "pending_approval" || a === "pending" || a === "";
}

export function isApproved(item: StudioItem): boolean {
  return (item.approvalStatus || "").toLowerCase() === "approved";
}

export function isRejected(item: StudioItem): boolean {
  return (item.approvalStatus || "").toLowerCase() === "rejected";
}

export function isPublished(item: StudioItem): boolean {
  return (item.status || "").toUpperCase() === "PUBLISHED" || !!item.publishedAt;
}

export function isFailed(item: StudioItem): boolean {
  const s = (item.status || "").toUpperCase();
  return s === "PUBLISH_FAILED" || s === "FAILED";
}

export function stageOf(item: StudioItem, now: number = Date.now()): Stage {
  if (isRejected(item)) return "rejected";
  if (isPublished(item)) return "published";
  if (isPending(item)) return "needs_review";
  if (isFailed(item)) return "failed";
  const when = toDate(item.scheduledAt);
  if (when) return when.getTime() < now ? "overdue" : "scheduled";
  return "approved";
}

export function stageLabel(item: StudioItem, stage: Stage = stageOf(item)): string {
  const kind = kindOf(item);
  switch (stage) {
    case "needs_review": return "Waiting for you";
    case "rejected": return "Rejected";
    case "published": return "Published";
    case "failed": return "Couldn't post";
    case "overdue": return "Overdue, not sent";
    case "scheduled": return `Scheduled ${fmtDate(item.scheduledAt)}`;
    case "approved":
      if (kind === "article") return "Approved, not published";
      if (kind === "social") return "Approved, not scheduled";
      return "Approved";
  }
}

export function stageClasses(stage: Stage): string {
  switch (stage) {
    case "needs_review": return "bg-amber-500/15 text-amber-300 border-amber-500/30";
    case "approved": return "bg-sky-500/15 text-sky-300 border-sky-500/30";
    case "scheduled": return "bg-blue-500/15 text-blue-300 border-blue-500/30";
    case "overdue": return "bg-red-500/15 text-red-300 border-red-500/30";
    case "failed": return "bg-red-500/15 text-red-300 border-red-500/30";
    case "published": return "bg-emerald-500/15 text-emerald-300 border-emerald-500/30";
    case "rejected": return "bg-zinc-500/15 text-zinc-300 border-zinc-500/30";
  }
}

// ── Text helpers ────────────────────────────────────────────────────────────

export function decodeEntities(s: string): string {
  if (!s || !/&[#a-z0-9]+;/i.test(s)) return s;
  if (typeof document === "undefined") return s;
  const ta = document.createElement("textarea");
  ta.innerHTML = s;
  return ta.value;
}

export function stripTags(s: string): string {
  return s.replace(/<!--[\s\S]*?-->/g, " ").replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim();
}

export function stripMarkdown(s: string): string {
  return s
    .replace(/^\s{0,3}#{1,6}\s*/, "")
    .replace(/\[([^\]]+)\]\([^)]*\)/g, "$1")
    .replace(/[*_`>]/g, "")
    .replace(/\s+/g, " ")
    .trim();
}

export function truncate(s: string, n: number): string {
  if (s.length <= n) return s;
  const cut = s.slice(0, n);
  const lastSpace = cut.lastIndexOf(" ");
  return `${(lastSpace > n * 0.6 ? cut.slice(0, lastSpace) : cut).trim()}…`;
}

export function looksLikeHtml(s: string): boolean {
  const t = (s || "").trimStart();
  if (!t) return false;
  if (/^<!doctype\s+html/i.test(t) || /^<html[\s>]/i.test(t)) return true;
  if (/^<(article|section|div|main|header|h[1-6]|p|ul|ol|table|aside|body)\b/i.test(t)) return true;
  if (/^<!--/.test(t) && /<\/?(p|h[1-6]|div|article|section)\b/i.test(t)) return true;
  const blocks = t.match(/<\/?(p|h[1-6]|div|article|section|ul|ol|li|table|aside|blockquote)\b[^>]*>/gi);
  return !!blocks && blocks.length >= 4;
}

function htmlTitle(html: string): string | null {
  const m = html.match(/<h1\b[^>]*>([\s\S]*?)<\/h1>/i) || html.match(/<title\b[^>]*>([\s\S]*?)<\/title>/i);
  if (!m) return null;
  const t = decodeEntities(stripTags(m[1])).trim();
  return t || null;
}

function firstLine(s: string): string {
  for (const line of s.split(/\r?\n/)) {
    const t = line.trim();
    if (t && !t.startsWith("<")) return t;
  }
  return "";
}

/** The text body of an item, whichever column the writer used. */
export function bodyOf(item: StudioItem): string {
  const doc = (item.documentContent || "").trim();
  const cap = (item.caption || "").trim();
  return doc ? doc : cap;
}

/** Human title: explicit title, else a short caption, else the first heading/line. */
export function deriveTitle(item: StudioItem): string {
  const explicit = (item.title || "").trim();
  if (explicit) return explicit;

  const caption = (item.caption || "").trim();
  const doc = (item.documentContent || "").trim();
  const kind = kindOf(item);
  const capIsHtml = looksLikeHtml(caption);

  // New shape: caption holds just the title (articles today; packages after the server change).
  if (caption && !capIsHtml && !caption.includes("\n") && (doc || kind !== "social") && caption.length <= 240) {
    return truncate(stripMarkdown(decodeEntities(caption)), 160);
  }
  if (capIsHtml) {
    const t = htmlTitle(caption);
    if (t) return truncate(t, 160);
  }
  if (doc && looksLikeHtml(doc)) {
    const t = htmlTitle(doc);
    if (t) return truncate(t, 160);
  }
  const heading = caption.match(/^\s{0,3}#{1,2}\s+(.+)$/m) || doc.match(/^\s{0,3}#{1,2}\s+(.+)$/m);
  if (heading && kind !== "social") return truncate(stripMarkdown(heading[1]), 160);

  const line = firstLine(caption) || firstLine(doc);
  if (line) return truncate(stripMarkdown(decodeEntities(line)), kind === "social" ? 100 : 160);
  return `No text yet (${typeLabel(item)})`;
}

// ── Word count ──────────────────────────────────────────────────────────────

export function countWords(text: string): number {
  if (!text) return 0;
  const cleaned = text
    .replace(/<!--[\s\S]*?-->/g, " ")
    .replace(/<(script|style)\b[\s\S]*?<\/\1>/gi, " ")
    .replace(/<[^>]+>/g, " ")
    .replace(/&[a-z#0-9]+;/gi, " ");
  let n = 0;
  for (const w of cleaned.split(/\s+/)) if (/[A-Za-z0-9À-ɏ]/.test(w)) n++;
  return n;
}

const wordCache = new Map<string, number>();

export function wordCountOf(item: StudioItem): number {
  if (typeof item.wordCount === "number" && item.wordCount >= 0) return item.wordCount;
  const body = bodyOf(item);
  const key = `${item.id}:${body.length}`;
  const hit = wordCache.get(key);
  if (hit !== undefined) return hit;
  const n = countWords(body);
  wordCache.set(key, n);
  return n;
}

// ── QA ──────────────────────────────────────────────────────────────────────

export interface QaCheck {
  name: string;
  ok: boolean;
  detail?: string;
}

/** One of the separate gates the worker recorded (pipeline / industry / supplemental ...). */
export interface QaGate {
  label: string;
  score: number | null;
  passed: boolean | null;
}

export interface QaSummary {
  score: number | null;
  /** true / false when the worker recorded a verdict, null when it only recorded numbers. */
  passed: boolean | null;
  checksPassed: number | null;
  checksFailed: number | null;
  failing: string[];
  /** Every individual check that carried a pass/fail verdict (failed ones first). */
  checks: QaCheck[];
  /** Per-gate scores such as pipelineScore / industryScore / supplementalPassed. */
  gates: QaGate[];
  checkedAt: string | null;
}

const EMPTY_QA = { checksPassed: null, checksFailed: null, failing: [] as string[], checks: [] as QaCheck[], gates: [] as QaGate[], checkedAt: null };

/**
 * Plain-language names for the pre-publish and after-publish checks whose raw ids read badly when only sentence-cased
 * ("jsonld_parse" -> "Jsonld parse"). Anything not listed here falls back to the generic rule below.
 */
const CHECK_LABELS: Record<string, string> = {
  enactment_tripwire: "Law-status tripwire",
  slug_collision: "Web address not already used",
  heading_prefix_leak: "No stray H1: / H2: in headings",
  frontmatter_leak: "No leftover front matter",
  jsonld_parse: "Structured data (JSON-LD) parses",
  internal_links: "Internal links work",
  focus_keyword: "Focus keyword",
  disclaimer_count: "Disclaimer count",
  independent_qa: "Independent QA",
  qa_pipeline: "Quality pipeline",
  update_target: "Update target is the live post",
  update_base: "Update base unchanged",
  plan_match: "Matches the plan",
  utm_link: "Tracking link (UTM)",
  stale_figures: "No out-of-date figures",
  sources_ledger: "Sources ledger",
  no_chatter: "No agent chatter in the text",
  no_script: "No scripts in the article",
  reviewed_by: "Reviewed-by line",
  standing_hold: "Not on the standing hold list",
  gov_links: "Government source links",
  // after-publish verification (the live page, re-fetched)
  http_ok: "Page loads (HTTP 200)",
  title_in_head: "Title tag present",
  jsonld_in_script: "Structured data inside script tags",
  no_body_bleed: "No stray code in the article body",
  no_storage_paths: "No internal file paths in the page",
  cta: "Call to action present",
  jsonld_count: "Structured data blocks",
};

/** "word_count_min" or "wordCountMin" becomes "Word count min". */
export function humanizeCheckName(name: string): string {
  const known = CHECK_LABELS[String(name).trim().toLowerCase()];
  if (known) return known;
  const spaced = String(name)
    .replace(/[_-]+/g, " ")
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .toLowerCase()
    .replace(/\s+/g, " ")
    .trim();
  return spaced ? spaced[0].toUpperCase() + spaced.slice(1) : String(name);
}

function checkVerdict(v: unknown): boolean | null {
  if (typeof v === "boolean") return v;
  if (v && typeof v === "object") {
    const o = v as Record<string, unknown>;
    for (const k of ["passed", "pass", "ok"]) if (typeof o[k] === "boolean") return o[k] as boolean;
    if (typeof o.status === "string") {
      const s = o.status.toLowerCase();
      if (s === "pass" || s === "passed" || s === "ok") return true;
      if (s === "fail" || s === "failed") return false;
    }
  }
  return null;
}

function checkDetail(c: unknown): string | undefined {
  if (!c || typeof c !== "object") return undefined;
  const o = c as Record<string, unknown>;
  for (const k of ["detail", "message", "reason", "note"]) {
    const v = o[k];
    if (typeof v === "string" && v.trim()) return v.trim();
  }
  return undefined;
}

function collectChecks(node: unknown, out: QaCheck[]): void {
  if (!node) return;
  if (Array.isArray(node)) {
    node.forEach((c, i) => {
      const ok = checkVerdict(c);
      if (ok === null) return;
      const name = c && typeof c === "object" ? String((c as any).name ?? (c as any).id ?? `check ${i + 1}`) : `check ${i + 1}`;
      out.push({ name, ok, detail: checkDetail(c) });
    });
  } else if (typeof node === "object") {
    for (const [name, c] of Object.entries(node as Record<string, unknown>)) {
      const ok = checkVerdict(c);
      if (ok !== null) out.push({ name, ok, detail: checkDetail(c) });
    }
  }
}

/** Reads `sourceContext.qa`. Returns null when the item has no QA record at all. */
export function parseQa(item: StudioItem): QaSummary | null {
  const ctx = item.sourceContext as Record<string, any> | null | undefined;
  const qa = ctx && typeof ctx === "object" ? ctx.qa : undefined;
  if (qa === undefined || qa === null || qa === "") return null;

  if (typeof qa === "number") return { score: qa, passed: null, ...EMPTY_QA };
  if (typeof qa === "boolean") return { score: null, passed: qa, ...EMPTY_QA };
  if (typeof qa === "string") {
    const s = qa.toLowerCase();
    const passed = /pass/.test(s) ? true : /fail/.test(s) ? false : null;
    return { score: null, passed, ...EMPTY_QA };
  }
  if (typeof qa !== "object") return null;

  const num = (v: unknown) => (typeof v === "number" && Number.isFinite(v) ? v : null);
  // The worker records the pipeline and industry scores separately; either is the headline number.
  const score = num(qa.score) ?? num(qa.qaScore) ?? num(qa.overall) ?? num(qa.industryScore) ?? num(qa.pipelineScore);
  let passed: boolean | null = null;
  for (const k of ["passed", "pass", "ok"]) if (typeof qa[k] === "boolean") { passed = qa[k]; break; }
  if (passed === null && typeof qa.status === "string") {
    const s = qa.status.toLowerCase();
    passed = /pass/.test(s) ? true : /fail/.test(s) ? false : null;
  }

  const checks: QaCheck[] = [];
  collectChecks(qa.checks, checks);
  collectChecks(qa.industry?.checks, checks);
  collectChecks(qa.supplemental?.checks, checks);
  collectChecks(qa.pipeline?.checks, checks);

  const failing: string[] = Array.isArray(qa.failing)
    ? qa.failing.map((f: any) => (typeof f === "string" ? f : String(f?.name ?? f?.id ?? ""))).filter(Boolean)
    : [];
  for (const c of checks) if (!c.ok && !failing.includes(c.name)) failing.push(c.name);
  // Gate flags such as `supplementalPassed: false` name a gate that did not pass.
  for (const [k, v] of Object.entries(qa)) {
    const m = /^([a-z]+)Passed$/.exec(k);
    if (m && v === false && !failing.includes(`${m[1]} gate`)) failing.push(`${m[1]} gate`);
  }

  // Gate scores: pipelineScore / pipelinePassed, industryScore / industryPassed, supplementalPassed ...
  const gates: QaGate[] = [];
  const gateNames = new Set<string>();
  for (const k of Object.keys(qa)) {
    const m = /^([a-z]+)(Score|Passed)$/.exec(k);
    if (m && m[1] !== "qa" && m[1] !== "overall") gateNames.add(m[1]);
  }
  gateNames.forEach((g) => {
    const s = num(qa[`${g}Score`]);
    const p = typeof qa[`${g}Passed`] === "boolean" ? (qa[`${g}Passed`] as boolean) : null;
    if (s !== null || p !== null) gates.push({ label: humanizeCheckName(g), score: s, passed: p });
  });

  const checksPassed = checks.length ? checks.filter((c) => c.ok).length : null;
  const checksFailed = checks.length ? checks.filter((c) => !c.ok).length : failing.length > 0 ? failing.length : null;
  const checkedAt = typeof qa.checkedAt === "string" ? qa.checkedAt : null;
  if (passed === null && checks.length === 0 && failing.length === 0 && score === null && gates.length === 0) {
    // Something is recorded but we cannot read a verdict from it.
    return { score: null, passed: null, ...EMPTY_QA, checkedAt };
  }
  const ordered = [...checks.filter((c) => !c.ok), ...checks.filter((c) => c.ok)];
  return { score, passed, checksPassed, checksFailed, failing, checks: ordered, gates, checkedAt };
}

/** One-line QA summary for rows and headers. */
export function qaShort(qa: QaSummary | null): string {
  if (!qa) return "Not scored";
  const score = qa.score !== null ? ` ${Math.round(qa.score)}/100` : "";
  if (qa.passed === true) return `QA passed${score}`;
  if (qa.passed === false) return `QA failed${score}`;
  if (qa.score !== null) return `QA${score}`;
  return "QA recorded";
}

export function qaDetail(qa: QaSummary | null): string {
  if (!qa) return "not scored";
  const parts: string[] = [];
  if (qa.score !== null) parts.push(`${Math.round(qa.score)}/100`);
  if (qa.passed === true) parts.push("passed");
  else if (qa.passed === false) parts.push("failed");
  if (qa.checksPassed !== null || qa.checksFailed !== null) {
    const p = qa.checksPassed ?? 0;
    const f = qa.checksFailed ?? 0;
    parts.push(`${p} check${p === 1 ? "" : "s"} passed, ${f} failed`);
  }
  return parts.length ? parts.join(" · ") : "recorded";
}

export function qaTone(qa: QaSummary | null): string {
  if (!qa) return "bg-zinc-500/15 text-zinc-300 border-zinc-500/30";
  if (qa.passed === false || (qa.checksFailed ?? 0) > 0) return "bg-red-500/15 text-red-300 border-red-500/30";
  if (qa.passed === true) return "bg-emerald-500/15 text-emerald-300 border-emerald-500/30";
  return "bg-sky-500/15 text-sky-300 border-sky-500/30";
}

/** Sources count from the item's own metadata (the ledger comment is counted separately). */
export function sourcesCountFromContext(item: StudioItem): number | null {
  const ctx = item.sourceContext as Record<string, any> | null | undefined;
  if (!ctx || typeof ctx !== "object") return null;
  if (typeof ctx.sourcesCount === "number") return ctx.sourcesCount;
  if (Array.isArray(ctx.sources)) return ctx.sources.length;
  if (typeof ctx.qa?.metrics?.sources === "number") return ctx.qa.metrics.sources;
  return null;
}

/** Rejection reason, if the server stored one. */
export function rejectionReasonOf(item: StudioItem): string | null {
  const ctx = item.sourceContext as Record<string, any> | null | undefined;
  const r = ctx && typeof ctx === "object" ? ctx.rejectionReason ?? ctx.rejection_reason : null;
  const direct = (item as any).rejectionReason;
  const val = typeof direct === "string" && direct ? direct : typeof r === "string" ? r : null;
  return val && val.trim() ? val.trim() : null;
}

/** The worker's own one-line reason for writing this now, when it recorded one. */
export function whyNowOf(item: StudioItem): string | null {
  const ctx = item.sourceContext as Record<string, any> | null | undefined;
  const v = ctx && typeof ctx === "object" ? ctx.whyNow : null;
  return typeof v === "string" && v.trim() ? v.trim() : null;
}

/** Search description: the column if set, else what the worker stored in sourceContext. */
export function metaDescriptionOf(item: StudioItem): string | null {
  if (item.metaDescription && item.metaDescription.trim()) return item.metaDescription.trim();
  const ctx = item.sourceContext as Record<string, any> | null | undefined;
  const v = ctx && typeof ctx === "object" ? ctx.metaDescription : null;
  return typeof v === "string" && v.trim() ? v.trim() : null;
}

/** Short verdict word for the Checks panel header. */
export function qaVerdict(qa: QaSummary | null): { label: string; tone: "good" | "bad" | "neutral" } {
  if (!qa) return { label: "Not scored", tone: "neutral" };
  if (qa.passed === false || (qa.checksFailed ?? 0) > 0 || qa.gates.some((g) => g.passed === false)) return { label: "Did not pass", tone: "bad" };
  if (qa.passed === true) return { label: "Passed", tone: "good" };
  if (qa.score !== null || qa.gates.length > 0) return { label: "Scored", tone: "neutral" };
  return { label: "Recorded", tone: "neutral" };
}

// ── "Could not verify" ──────────────────────────────────────────────────────

export function toLines(v: unknown): string[] {
  if (v === null || v === undefined || v === "") return [];
  if (typeof v === "string") {
    return v
      .split(/\r?\n/)
      .map((l) => l.replace(/^\s*(?:[-*•]|\d+[.)])\s*/, "").trim())
      .filter(Boolean);
  }
  if (Array.isArray(v)) return v.flatMap((x) => toLines(x));
  if (typeof v === "object") {
    const o = v as Record<string, unknown>;
    for (const k of ["claim", "text", "fact", "statement", "note", "reason", "item", "description", "name"]) {
      if (typeof o[k] === "string" && (o[k] as string).trim()) {
        const extra = typeof o.reason === "string" && k !== "reason" && (o.reason as string).trim() ? ` (${(o.reason as string).trim()})` : "";
        return [`${(o[k] as string).trim()}${extra}`];
      }
    }
  }
  return [];
}

/**
 * Things the writer says it could not confirm: `sourceContext.unverified`, else `sourceContext.notes`,
 * else the QA record's own unverified list. Always plain strings, capped in length.
 */
export function unverifiedOf(item: StudioItem): string[] {
  const ctx = item.sourceContext as Record<string, any> | null | undefined;
  if (!ctx || typeof ctx !== "object") return [];
  const raw = [ctx.unverified, ctx.notes, ctx.qa?.unverified, ctx.qa?.metrics?.unverified_figures];
  for (const v of raw) {
    const lines = toLines(v);
    if (lines.length) return lines.map((l) => truncate(l, 400)).slice(0, 25);
  }
  return [];
}

// ── Destination ─────────────────────────────────────────────────────────────

/** Where an item goes when it is published, in plain words. `connected` drives the warning tone. */
export function destinationOf(
  item: StudioItem,
  workspace: any,
  connected: { wordpress: boolean; platform: boolean },
): { text: string; ok: boolean | null } {
  const kind = kindOf(item);
  if (kind === "article") {
    let host = "";
    try {
      host = workspace?.wordpressUrl ? new URL(String(workspace.wordpressUrl)).host : "";
    } catch {
      host = String(workspace?.wordpressUrl || "");
    }
    return connected.wordpress
      ? { text: host ? `Your WordPress site (${host})` : "Your WordPress site", ok: true }
      : { text: "WordPress, which isn't connected yet", ok: false };
  }
  if (kind === "social") {
    const name = platformLabel(item);
    return connected.platform ? { text: `${name} (connected)`, ok: true } : { text: `${name}, which isn't connected yet`, ok: false };
  }
  return { text: "Stays in Content Studio (a working document)", ok: null };
}
